/**
 * The print queue — a single slot, because only one document can be on the
 * paper at a time.
 *
 * "Every print button opens an on-screen preview: white paper, black text,
 * dashed lines where pages break, Back and Print. Nothing goes to the printer
 * unseen."
 *
 * Print buttons are scattered deep in the tree — the Report tab, the signature
 * popup, later the pay run — so this is a small store rather than props threaded
 * through six components. Same shape as `session.ts`.
 *
 * Documents are rendered CLIENT-SIDE from data the client already holds. The
 * Worker has a 10ms CPU budget and must never build a report by looping over
 * rows.
 */

import type { ReactNode } from 'react';

export interface PrintJob {
  /** Shown next to the Print button, and used as the document title. */
  label: string;
  document: ReactNode;
}

type Listener = (job: PrintJob | null) => void;

let current: PrintJob | null = null;
const listeners = new Set<Listener>();

export function subscribePrint(listener: Listener): () => void {
  listeners.add(listener);
  listener(current);
  return () => listeners.delete(listener);
}

export function openPrint(label: string, document: ReactNode): void {
  current = { label, document };
  for (const listener of listeners) listener(current);
}

export function closePrint(): void {
  current = null;
  for (const listener of listeners) listener(null);
}

export function getPrintJob(): PrintJob | null {
  return current;
}
